<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import WorkingScheduleForm from './WorkingScheduleForm.vue';
import ProvetAPI from 'dashboard/api/provet';

const { t } = useI18n();

const loading = ref({});
const schedules = ref([]);
const isFetching = ref(false);
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const selectedSchedule = ref(null);
const formMode = ref('create');

const tableHeaders = computed(() => {
  return [
    t('PROVET_SETTINGS.WORKING_SCHEDULES.TABLE.DATE'),
    t('PROVET_SETTINGS.WORKING_SCHEDULES.TABLE.START_TIME'),
    t('PROVET_SETTINGS.WORKING_SCHEDULES.TABLE.END_TIME'),
    t('PROVET_SETTINGS.WORKING_SCHEDULES.TABLE.AUTORESPONSE'),
    t('PROVET_SETTINGS.WORKING_SCHEDULES.TABLE.ACTIONS'),
  ];
});

const formatTime = (minutes) => {
  if (minutes === null || minutes === undefined) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatDate = (schedule) => {
  if (schedule.modificator === 'One' && schedule.date && schedule.date !== '*') {
    try {
      const date = new Date(schedule.date);
      return date.toLocaleDateString('en-GB');
    } catch {
      return schedule.date;
    }
  }

  const parts = [schedule.modificator, schedule.weekday]
    .filter(Boolean)
    .map(value => (value === 'Any' ? 'day' : value));
  if (parts.length) return parts.join(' ');

  if (!schedule.date || schedule.date === '*') return t('PROVET_SETTINGS.WORKING_SCHEDULES.TABLE.ANY_DATE');
  try {
    const date = new Date(schedule.date);
    return date.toLocaleDateString('en-GB');
  } catch {
    return schedule.date;
  }
};

const formatAutoresponsePreview = (autoresponse) => {
  if (!autoresponse) return '-';
  if (autoresponse.length <= 32) return autoresponse;
  return `${autoresponse.slice(0, 32)}...`;
};

const fetchSchedules = async () => {
  isFetching.value = true;
  try {
    const response = await ProvetAPI.getWorkingSchedules();
    schedules.value = response.data?.payload || [];
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('PROVET_SETTINGS.WORKING_SCHEDULES.FETCH.ERROR'),
      'error'
    );
  } finally {
    isFetching.value = false;
  }
};

const openAddModal = () => {
  formMode.value = 'create';
  selectedSchedule.value = null;
  showAddModal.value = true;
};

const closeAddModal = () => {
  showAddModal.value = false;
  selectedSchedule.value = null;
};

const openEditModal = (schedule) => {
  formMode.value = 'edit';
  selectedSchedule.value = schedule;
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  selectedSchedule.value = null;
};

const openDeleteModal = (schedule) => {
  selectedSchedule.value = schedule;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  selectedSchedule.value = null;
};

const handleFormSuccess = () => {
  fetchSchedules();
};

const confirmDelete = async () => {
  if (!selectedSchedule.value) return;

  loading.value[selectedSchedule.value.id] = true;
  try {
    await ProvetAPI.deleteWorkingSchedule(selectedSchedule.value.id);
    useAlert(t('PROVET_SETTINGS.WORKING_SCHEDULES.DELETE.SUCCESS'));
    closeDeleteModal();
    fetchSchedules();
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('PROVET_SETTINGS.WORKING_SCHEDULES.DELETE.ERROR');
    useAlert(errorMessage, 'error');
  } finally {
    loading.value[selectedSchedule.value.id] = false;
  }
};

onMounted(() => {
  fetchSchedules();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <h3 class="text-base font-semibold">
        {{ $t('PROVET_SETTINGS.WORKING_SCHEDULES.TITLE') }}
      </h3>
      <Button
        icon="i-lucide-circle-plus"
        :label="$t('PROVET_SETTINGS.WORKING_SCHEDULES.ADD_BUTTON')"
        @click="openAddModal"
      />
    </div>

    <woot-loading-state
      v-if="isFetching"
      :message="$t('PROVET_SETTINGS.WORKING_SCHEDULES.LOADING')"
    />

    <p
      v-else-if="!schedules.length"
      class="flex flex-col items-center justify-center py-8 text-n-slate-11"
    >
      {{ $t('PROVET_SETTINGS.WORKING_SCHEDULES.EMPTY_STATE') }}
    </p>

    <table v-else class="min-w-full overflow-x-auto divide-y divide-n-weak">
      <thead>
        <tr>
          <th
            v-for="header in tableHeaders"
            :key="header"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
        <tr v-for="schedule in schedules" :key="schedule.id">
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            <span class="mb-1 font-medium break-words text-n-slate-12">
              {{ formatDate(schedule) }}
            </span>
          </td>
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            {{ formatTime(schedule.start_time) }}
          </td>
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            {{ formatTime(schedule.end_time) }}
          </td>
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            <span :title="schedule.autoresponse || ''">
              {{ formatAutoresponsePreview(schedule.autoresponse) }}
            </span>
          </td>
          <td class="py-4 min-w-xs">
            <div class="flex gap-1 justify-end">
              <Button
                v-tooltip.top="$t('PROVET_SETTINGS.WORKING_SCHEDULES.EDIT')"
                icon="i-lucide-pen"
                slate
                xs
                faded
                :is-loading="loading[schedule.id]"
                @click="openEditModal(schedule)"
              />
              <Button
                v-tooltip.top="$t('PROVET_SETTINGS.WORKING_SCHEDULES.DELETE.PLACEHOLDER')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :is-loading="loading[schedule.id]"
                @click="openDeleteModal(schedule)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <woot-modal v-model:show="showAddModal" :on-close="closeAddModal">
      <WorkingScheduleForm
        mode="create"
        @close="closeAddModal"
        @success="handleFormSuccess"
      />
    </woot-modal>

    <woot-modal v-model:show="showEditModal" :on-close="closeEditModal">
      <WorkingScheduleForm
        v-if="selectedSchedule"
        mode="edit"
        :selected-schedule="selectedSchedule"
        @close="closeEditModal"
        @success="handleFormSuccess"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDelete"
      :title="$t('PROVET_SETTINGS.WORKING_SCHEDULES.DELETE.CONFIRM.TITLE')"
      :message="$t('PROVET_SETTINGS.WORKING_SCHEDULES.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('PROVET_SETTINGS.WORKING_SCHEDULES.DELETE.CONFIRM.YES')"
      :reject-text="$t('PROVET_SETTINGS.WORKING_SCHEDULES.DELETE.CONFIRM.NO')"
    />
  </div>
</template>

